import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetReports, dataSetPrintServers } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IReport, Report } from '../../../@types/model/masterData/report/report';
import { IReportFormValues, ReportFormValues } from '../../../@types/model/masterData/report/reportFormValues';
import ReportForm from './form/ReportForm';
import ReportHttpService from '../../../services/http/masterData/reportHttpService';
import PrintServerHttpService from '../../../services/http/masterData/printServerHttpService';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setReportMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const ReportScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const reports = useAppSelector(x => x.masterData.reports);
    const printServers = useAppSelector(x => x.masterData.printServers);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState<IReport>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingReport, setDeletingReport] = useState<IReport>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [reportRes, printServerRes] = await Promise.all([
                        ReportHttpService.getReportData(),
                        PrintServerHttpService.getPrintServerData(),
                    ]);
                    dispatch(dataSetReports(reportRes.data));
                    dispatch(dataSetPrintServers(printServerRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading reports.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedReport(undefined);
        setFormGuid(undefined);
    }, []);

    const editReport = useCallback((report : IReport) => {
        setSelectedReport(report);
        setFormGuid(report.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IReportFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await ReportHttpService.addOrUpdateReport(Report(payload));
            const updatedList = upsertArrayElement(reports, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetReports(updatedList));
            await setReportMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedReport ? 'Report updated successfully.' : 'Report added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating report data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((report : IReport) => {
        setDeletingReport(report);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingReport(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removeReport = async () => {
        if (!deletingReport) return;
        const updatedReport = { ...deletingReport, isActive: false };
        setIsLoading(true);
        try {
            const res = await ReportHttpService.deleteReport(updatedReport.id);
            const updatedList = upsertArrayElement(reports, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetReports(updatedList));
            await setReportMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Report deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting report.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const getPrintServerName = useCallback((printServerId : number) => {
        const printServer = printServers.find(x => x.id === printServerId);
        return printServer ? printServer.name : '';
    }, [printServers]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return ReportFormValues(selectedReport, printServers);
        }
    }, [selectedReport, printServers, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Report'
                    isEdit={!!selectedReport}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    maxWidth={'md'}
                    fullWidth
                    onClose={closeDialog}>
                    <ReportForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IReport>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editReport}
                        enableEditing={hasEditingRight}
                        enableDeleting={report => report.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Print Server', field: 'printServerId', formatFunction: getPrintServerName, enableFiltering: true, enableSorting: true },
                            { title: 'Can Palletize?', field: 'canLivePalletize', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={reports}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                        fitWidthToPage
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Report'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this report?'
                onAccept={removeReport}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default ReportScreen;
